import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { CircularProgress, FormControl } from "@mui/material";
import { z } from "zod";

import { Patient, PatientBaseAttributes, DocumentType } from "@interfaces";
import { FormContainer } from "@styles/styled";
import { ErrorMessage, InputLabel, ObscuredSingleInput } from "./styled";
import {
  BirthdateInput,
  BottomActionArea,
  BottomActionButton,
  Header,
  InputFieldWrapper,
  SingleFormInput,
  scrollToTop,
  NavigateBeforeButton,
  PhoneInput,
  DocumentTypeSelection,
  FormBody,
  FormContentContainer,
  Autocomplete,
  WhiteTextField,
} from "@components";
import { useEditPatient } from "@hooks";
import {
  capitalizeParametValue,
  getBirthdateDetails,
  handleNumericInputChange,
  replaceCurrentPhoneCode,
  validateWeight,
} from "@utils";
import { validate as validateRut, format } from "rut.js";
import regionsWithCommunes from "../../data/regionsWithCommunes.json";
import { isValidPassport } from "@utils";
import React from "react";

const regions = regionsWithCommunes.map((region: any) => region.region);

function getEditPatientSchema(phoneRequired: boolean) {
  return z.object({
    rut: z.string().nullable().default(null),
    passport: z.string().nullable().default(null),
    name: z
      .string()
      .min(1, "Debes escribir el nombre del paciente.")
      .transform((s) => s.trim()),
    firstSurname: z
      .string()
      .min(1, "Debes escribir el apellido paterno del paciente.")
      .transform((s) => s.trim()),
    secondSurname: z
      .string()
      .min(1, "Debes escribir el apellido materno del paciente.")
      .transform((s) => s.trim()),
    birthdate: z
      .string()
      .min(1, "Debes ingresar la fecha de nacimiento del paciente."),
    weight: z
      .union([z.string(), z.number()])
      .nullable()
      .optional()
      .transform((v) => {
        if (v == null) return null;
        const s = String(v).trim();
        return s === "" ? null : s;
      }),
    email: z
      .string()
      .min(1, "Debes escribir el correo electrónico del paciente.")
      .email("El correo electrónico está incompleto")
      .transform((s) => s.toLowerCase().trim()),
    phone: z.string(),
    region: z.string().min(1, "Debes seleccionar la región del paciente."),
    commune: z.string().min(1, "Debes seleccionar la comuna del paciente."),
    street: z
      .string()
      .min(1, "Debes escribir la dirección del paciente.")
      .transform((s) => s.trim()),
  });
}

interface LocationState {
  patient?: Patient;
  prevRoute?: string;
  requestId?: string;
}

export function EditPatientInfo() {
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    setError,
    formState: { errors },
    clearErrors,
    trigger,
  } = useForm<PatientBaseAttributes>();

  const navigate = useNavigate();
  const location = useLocation();
  const {
    patient = {} as Patient,
    prevRoute = "",
    requestId = "",
  }: LocationState = location.state || {};
  const [formState, setFormState] = useState(patient);
  const [settingPatientData, setSettingPatientData] = useState<boolean>(true);
  const { mutate: editPatient, isPending } = useEditPatient(
    prevRoute,
    requestId,
  );

  // Document:
  const patientInputRut = watch("rut");
  const patientInputPassport = watch("passport");
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<DocumentType>(
      formState.rut ? DocumentType.RUT : DocumentType.PASSPORT,
    );
  const [showRutError, setShowRutError] = useState<boolean>(false);
  const [showPassportError, setShowPassportError] = useState<boolean>(false);
  const isDocumentInputEmpty = !patientInputRut && !patientInputPassport;
  const isDocumentInputValid =
    selectedDocumentType === DocumentType.RUT
      ? patientInputRut && validateRut(patientInputRut)
      : patientInputPassport && isValidPassport(patientInputPassport);

  // birthdate:
  const birthdate = watch("birthdate");
  const birthdateDetails = useMemo(() => {
    return getBirthdateDetails(birthdate);
  }, [birthdate]);

  // Regions and communes:
  const selectedRegion = watch("region");
  const selectedCommune = watch("commune");
  // Show only communes for selected region:
  const communes = useMemo(() => {
    const foundRegion = regionsWithCommunes.find(
      (r) => r.region === selectedRegion,
    );
    return foundRegion ? foundRegion.communes.sort() : [];
  }, [selectedRegion]);

  // Phone:
  const phoneInputRequired = Boolean(patient.phone);
  const [currentPhoneCode, setCurrentPhoneCode] = useState({
    name: "Chile",
    icon: "🇨🇱",
    prefix: "+56",
  });

  const weight = watch("weight");

  const validateAndSetInputValue = useCallback(
    (value: string, documentType: DocumentType) => {
      if (documentType === DocumentType.RUT) {
        if (validateRut(value)) {
          setValue("rut", format(value));
          setShowRutError(false);
          clearErrors("rut");
          return true;
        } else {
          setShowRutError(true);
          return false;
        }
      } else {
        if (isValidPassport(value)) {
          setValue("passport", value.toString().toUpperCase());
          setShowPassportError(false);
          clearErrors("passport");
          return true;
        } else {
          setShowPassportError(true);
          return false;
        }
      }
    },
    [setValue, clearErrors, setShowRutError, setShowPassportError],
  );

  // Handle back browsing:
  const handleBack = useCallback(() => {
    if (settingPatientData) {
      navigate(prevRoute);
    } else {
      setSettingPatientData(true);
    }
    scrollToTop();
  }, [settingPatientData, prevRoute, setSettingPatientData, navigate]);

  const handleDocumentTypeChange = useCallback(
    (documentType: DocumentType) => {
      setSelectedDocumentType(documentType);
      setValue("rut", "");
      setValue("passport", "");
      setShowRutError(false);
      setShowPassportError(false);
    },
    [setValue],
  );
  const handleDocumentNumberOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (selectedDocumentType === DocumentType.RUT) {
        setValue("rut", e.target.value);
        clearErrors("rut");
        setShowRutError(false);
      } else {
        setValue("passport", e.target.value);
        clearErrors("passport");
        setShowPassportError(false);
      }
    },
    [
      setValue,
      clearErrors,
      setShowRutError,
      setShowPassportError,
      selectedDocumentType,
    ],
  );

  const handleDocumentNumberBlur = useCallback(() => {
    if (isDocumentInputEmpty) return;

    setShowRutError(false);
    setShowPassportError(false);
    clearErrors("rut");
    clearErrors("passport");

    validateAndSetInputValue(
      selectedDocumentType === DocumentType.RUT
        ? patientInputRut || ""
        : patientInputPassport || "",
      selectedDocumentType,
    );
  }, [
    selectedDocumentType,
    validateAndSetInputValue,
    isDocumentInputEmpty,
    patientInputRut,
    patientInputPassport,
    setShowRutError,
    setShowPassportError,
    clearErrors,
  ]);

  const handleDocumentNumberKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter") return;
      if (isDocumentInputEmpty) return;
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      validateAndSetInputValue(
        selectedDocumentType === DocumentType.RUT
          ? patientInputRut || ""
          : patientInputPassport || "",
        selectedDocumentType,
      );
    },
    [
      patientInputRut,
      patientInputPassport,
      validateAndSetInputValue,
      isDocumentInputEmpty,
      selectedDocumentType,
    ],
  );

  const onPatientDataSubmit = useCallback(
    (formData: PatientBaseAttributes) => {
      setFormState((prevState) => {
        return {
          ...prevState,
          ...formData,
          weight: formData.weight,
          phone: phoneInputRequired
            ? replaceCurrentPhoneCode(formData.phone, currentPhoneCode.prefix)
            : "",
          region: formData.region !== "" ? formData.region : prevState.region,
        };
      });
      setSettingPatientData(false);
      scrollToTop();
    },
    [currentPhoneCode, phoneInputRequired, setFormState, setSettingPatientData],
  );

  const onSubmit: SubmitHandler<PatientBaseAttributes> = useCallback(
    async (data) => {
      const emptyToNull = (v: string | null | undefined) =>
        v != null && String(v).trim() !== "" ? v : null;
      const updateAttributes = {
        rut: emptyToNull(data.rut ?? null),
        passport: emptyToNull(data.passport ?? null),
        name: data.name,
        firstSurname: data.firstSurname,
        secondSurname: data.secondSurname,
        // birthdate input has an inconsistency with the typing
        birthdate: new Date(data.birthdate).toISOString(),
        weight: data.weight,
        email: data.email,
        phone: phoneInputRequired ? currentPhoneCode.prefix + data.phone : "",
        region: data.region,
        commune: data.commune,
        street: data.street,
      };
      const schema = getEditPatientSchema(phoneInputRequired);
      const parsed = schema.safeParse(updateAttributes);
      if (!parsed.success) {
        const zodErrors = parsed.error.flatten().fieldErrors;
        Object.entries(zodErrors).forEach(([field, message]) => {
          setError(field as keyof PatientBaseAttributes, {
            type: "manual",
            message: Array.isArray(message) ? message[0] : message,
          });
        });
        return;
      }
      editPatient({
        patientUuid: patient.uuid,
        updates: { ...parsed.data, weight: parsed.data.weight } as Patient,
      });
    },
    [currentPhoneCode, phoneInputRequired, patient.uuid, editPatient, setError],
  );

  // Fill in entries when the formState has already been established.
  useEffect(() => {
    if (formState.rut || formState.passport) {
      Object.entries(formState).forEach(([key, value]) => {
        if (key === "birthdate") {
          // birthdate input has an inconsistency with the typing
          setValue("birthdate", new Date(value).toISOString());
        } else {
          setValue(key as keyof PatientBaseAttributes, value);
        }
      });
    }
  }, [formState, setValue]);

  return (
    <>
      <Header
        title={settingPatientData ? "Datos del paciente" : "Datos de contacto"}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            {settingPatientData ? (
              <React.Fragment key="edit-patient-step1">
                <FormControl fullWidth>
                  <DocumentTypeSelection
                    value={selectedDocumentType}
                    onChange={handleDocumentTypeChange}
                    label="Tipo de documento"
                    disabled={true}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Número de documento</InputLabel>
                  <ObscuredSingleInput
                    placeholder={
                      selectedDocumentType === DocumentType.RUT
                        ? "12.345.678-9"
                        : "ABC123456789"
                    }
                    {...(selectedDocumentType === DocumentType.RUT
                      ? register("rut", {
                          required: "Debes escribir el RUT del paciente.",
                        })
                      : register("passport", {
                          required: "Debes escribir el Pasaporte del paciente.",
                        }))}
                    onBlur={handleDocumentNumberBlur}
                    onKeyDown={handleDocumentNumberKeyDown}
                    onChange={handleDocumentNumberOnChange}
                    error={
                      selectedDocumentType === DocumentType.RUT
                        ? showRutError || !!errors["rut"]
                        : showPassportError || !!errors["passport"]
                    }
                    inputProps={{
                      "data-openreplay-obscured": "",
                    }}
                  />

                  {selectedDocumentType === DocumentType.RUT && (
                    <>
                      {showRutError && !errors.rut && (
                        <ErrorMessage>RUT no válido</ErrorMessage>
                      )}
                      {!showRutError && errors.rut && (
                        <ErrorMessage>{errors.rut.message}</ErrorMessage>
                      )}
                    </>
                  )}

                  {selectedDocumentType === DocumentType.PASSPORT && (
                    <>
                      {showPassportError && !errors.passport && (
                        <ErrorMessage>Pasaporte no válido</ErrorMessage>
                      )}
                      {!showPassportError && errors.passport && (
                        <ErrorMessage>{errors.passport.message}</ErrorMessage>
                      )}
                    </>
                  )}
                </FormControl>

                <SingleFormInput
                  title="Nombre"
                  parameterName="name"
                  errorText="Debes escribir el nombre del paciente."
                  register={register}
                  errors={errors}
                  onBlur={() =>
                    capitalizeParametValue("name", watch, setValue, trigger)
                  }
                  obscureInTracker={true}
                />

                <SingleFormInput
                  title="Apellido paterno"
                  parameterName="firstSurname"
                  errorText="Debes escribir el apellido paterno del paciente."
                  register={register}
                  errors={errors}
                  onBlur={() =>
                    capitalizeParametValue(
                      "firstSurname",
                      watch,
                      setValue,
                      trigger,
                    )
                  }
                  obscureInTracker={true}
                />

                <SingleFormInput
                  title="Apellido materno"
                  parameterName="secondSurname"
                  errorText="Debes escribir el apellido materno del paciente."
                  register={register}
                  errors={errors}
                  autoComplete="off"
                  onBlur={() =>
                    capitalizeParametValue(
                      "secondSurname",
                      watch,
                      setValue,
                      trigger,
                    )
                  }
                  obscureInTracker={true}
                />

                <BirthdateInput
                  control={control}
                  errorManagement={{ errors }}
                  obscureInTracker={true}
                />

                {birthdateDetails.weightNeeded && (
                  <SingleFormInput
                    title="Peso"
                    parameterName="weight"
                    inputType="number"
                    numericInputType="decimal"
                    numericExternalValue={weight}
                    numericMaxValue={100}
                    handleNumericExternalValueChange={(value) => {
                      handleNumericInputChange(
                        "weight",
                        String(value),
                        clearErrors,
                        setValue,
                      );
                    }}
                    units="kg"
                    errorText="Debes ingresar el peso del paciente."
                    register={register}
                    errors={errors}
                    validate={(value: string) =>
                      validateWeight(value) ||
                      "Debes ingresar un peso menor a 100 kg"
                    }
                    obscureInTracker={true}
                  />
                )}

                <BottomActionArea>
                  <BottomActionButton
                    onClick={handleSubmit(onPatientDataSubmit)}
                    disabled={isDocumentInputEmpty || !isDocumentInputValid}
                  >
                    Siguiente
                  </BottomActionButton>
                </BottomActionArea>
              </React.Fragment>
            ) : (
              <React.Fragment key="edit-patient-step2">
                <SingleFormInput
                  title="Correo electrónico"
                  parameterName="email"
                  errorText="Debes escribir el correo electrónico del paciente."
                  // description="A este correo notificaremos al paciente."
                  register={register}
                  errors={errors}
                  inputType="email"
                  onBlur={() => {
                    const emailValue = watch("email");
                    if (emailValue) {
                      setValue("email", emailValue.toLowerCase());
                    }
                    trigger("email");
                  }}
                  validate={(value) => {
                    const emailRegex =
                      /^([a-zA-Z0-9_\-+.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/;
                    if (emailRegex.exec(value)) {
                      return true;
                    } else {
                      return "El correo electrónico está incompleto";
                    }
                  }}
                  obscureInTracker={true}
                />

                {phoneInputRequired && (
                  <PhoneInput
                    register={register}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    trigger={trigger}
                    clearErrors={clearErrors}
                    phoneCodeState={{
                      currentPhoneCode,
                      setCurrentPhoneCode,
                    }}
                    obscureInTracker={true}
                  />
                )}

                <InputFieldWrapper
                  label="Región"
                  error={errors.region?.message}
                >
                  <Controller
                    name={"region"}
                    control={control}
                    defaultValue="Metropolitana de Santiago"
                    rules={{
                      required: "Debes seleccionar la región del paciente.",
                    }}
                    render={({ ...props }) => (
                      <Autocomplete
                        disablePortal
                        value={selectedRegion}
                        options={regions}
                        getOptionLabel={(region) => region}
                        renderInput={(params) => (
                          <WhiteTextField
                            error={Boolean(errors.region)}
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              "data-openreplay-obscured": "",
                            }}
                          />
                        )}
                        onChange={(_, data) => {
                          setValue("commune", "");
                          if (data) clearErrors("region");
                          props.field.onChange(data);
                        }}
                      />
                    )}
                  />
                </InputFieldWrapper>

                <InputFieldWrapper
                  label="Comuna"
                  error={errors.commune?.message}
                >
                  <Controller
                    name={"commune"}
                    control={control}
                    rules={{
                      required: "Debes seleccionar la comuna del paciente.",
                    }}
                    render={({ ...props }) => (
                      <Autocomplete
                        disablePortal
                        value={selectedCommune}
                        options={communes}
                        getOptionLabel={(commune) => commune}
                        renderInput={(params) => (
                          <WhiteTextField
                            error={Boolean(errors.commune)}
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              "data-openreplay-obscured": "",
                            }}
                          />
                        )}
                        onChange={(_, data) => {
                          if (data) clearErrors("commune");
                          props.field.onChange(data);
                        }}
                      />
                    )}
                  />
                </InputFieldWrapper>

                <SingleFormInput
                  title="Dirección"
                  parameterName="street"
                  errorText="Debes escribir la dirección del paciente."
                  // description="Ej: Av. Providencia 123"
                  register={register}
                  errors={errors}
                  obscureInTracker={true}
                />

                <BottomActionArea>
                  <BottomActionButton
                    type="submit"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isPending}
                  >
                    {isPending ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Guardar"
                    )}
                  </BottomActionButton>
                </BottomActionArea>
              </React.Fragment>
            )}
          </FormContainer>
        </FormBody>
      </FormContentContainer>
    </>
  );
}
